import {
  ChangeDetectorRef,
  Component,
  ComponentRef,
  ElementRef,
  Input,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { BookingDTO, CashDrawerActionDTO, OrderDTO } from '@lkp/parking-sdk';
import { Subject, takeUntil } from 'rxjs';
import { ConfirmDialogComponent } from 'src/app/components/confirm-dialog/confirm-dialog.component';
import { ErrorDialogComponent } from 'src/app/components/error-dialog/error-dialog.component';
import { ReceiptComponent } from 'src/app/components/receipt/receipt.component';
import { IframeService } from 'src/app/core/services/iframe.service';
import { OrderService } from 'src/app/core/services/order.service';
import { GetOrder } from 'src/app/core/states/orders/orders.action';

@Component({
    selector: 'app-order-summary',
    templateUrl: './order-summary.component.html',
    styleUrls: ['./order-summary.component.scss'],
    standalone: false
})
export class OrderSummaryComponent implements OnDestroy {
  @ViewChild('iframesummary', { read: ElementRef, static: false })
  iframeSummary: ElementRef | undefined;
  @ViewChild('iframereceipt', { read: ElementRef, static: false })
  iframeReceipt: ElementRef | undefined;
  @Input() order?: OrderDTO;
  @Input() currentBooking?: BookingDTO;
  @Input() hasUnfinishedPayments = false;
  @Input() isDriverMode = false;
  doc: any;
  dynamicRef?: ComponentRef<ReceiptComponent>;
  cancelledBookings = false;
  emailMsg?: string;
  emailSent?: boolean;
  loading = false;
  readonly onDestroy$ = new Subject<void>();

  constructor(
    readonly router: Router,
    readonly store: Store,
    readonly viewContainerRef: ViewContainerRef,
    readonly iframeService: IframeService,
    readonly cdr: ChangeDetectorRef,
    readonly dialog: MatDialog,
    readonly orderService: OrderService
  ) {}

  ngOnInit() {
    this.cancelledBookings = this.order?.orderItems
      ? this.orderService.allOrderBookingsCancelled(this.order.orderItems)
      : false;
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.cancelledBookings = this.order?.orderItems
      ? this.orderService.allOrderBookingsCancelled(this.order.orderItems)
      : false;
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
    this.order = undefined;
  }

  calculateTotalDiscount(): number {
    if (this.order)
      return (
        (this.order.totalAmountWithoutDiscount || 0) -
        (this.order.totalAmount || 0)
      );
    else return 0;
  }

  goToCheckout(): void {
    if (this.order?.orderId !== undefined)
      this.store
        .dispatch(new GetOrder(this.order.orderId))
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((v) => this.router.navigate(['hantera-betalning']));
  }

  onLoad() {
    if (this.iframeSummary) {
      this.doc = this.iframeSummary.nativeElement.contentWindow.document;
      this.createComponent();
    } else {
      setTimeout(() => {
        this.doc = this.iframeSummary!.nativeElement.contentWindow.document;
        this.createComponent();
      });
    }
  }

  reloadReceipt() {
    if (this.dynamicRef) {
      this.dynamicRef.instance.order = this.order;
      this.dynamicRef.changeDetectorRef.detectChanges();
    }
  }

  createComponent() {
    this.dynamicRef = this.viewContainerRef.createComponent(ReceiptComponent);
    this.dynamicRef.instance.order = this.order;
    this.dynamicRef.changeDetectorRef.detectChanges();
    this.doc.body.appendChild(this.dynamicRef.location.nativeElement);
    this.iframeService.attachStyles(this.doc);
  }

  awaitReceiptChanges(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.cdr.detectChanges();
      setTimeout(() => {
        resolve();
      }, 0);
    });
  }

  printSummary() {
    (
      document.getElementById('iframesummary') as HTMLIFrameElement
    ).contentWindow?.print();
  }

  havePrepaidTicket(order: OrderDTO): boolean {
    const prepaidTicketOrderItems = order.orderItems?.filter(
      (orderItem) =>
        orderItem.orderItemType === 'RESOURCE' &&
        orderItem.booking &&
        orderItem.booking.prepaidTicket
    );
    return prepaidTicketOrderItems!.length > 0;
  }
}
